export const TUTOR_DETAILS_KEYS = {
    organizationName: "tutorOrganizationName",
    logoUrl: "tutorLogoUrl",
    guideSeen: "tutorGuideSeen",
    groups: "tutorGroups",
    library: "tutorLibrary",
};

function asArray(value) {
    return Array.isArray(value) ? value : [];
}

export function newTutorId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `tutor-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function sanitizeGroupParents(groups) {
    const byId = new Map(groups.map((group) => [group.id, { ...group }]));
    const state = new Map();

    const visit = (id) => {
        if (state.get(id) === "done" || !byId.has(id)) {
            return;
        }
        state.set(id, "visiting");
        const node = byId.get(id);
        const parentId = node.parentId;
        if (!parentId || parentId === id || !byId.has(parentId) || state.get(parentId) === "visiting") {
            node.parentId = null;
        } else {
            visit(parentId);
        }
        state.set(id, "done");
    };

    groups.forEach((group) => visit(group.id));
    return groups.map((group) => byId.get(group.id));
}

export function readTutorGroups(details) {
    const seen = new Set();
    const groups = [];
    asArray(details?.[TUTOR_DETAILS_KEYS.groups]).forEach((group) => {
        if (!group || !group.id) {
            return;
        }
        const id = String(group.id);
        if (seen.has(id)) {
            return;
        }
        seen.add(id);
        groups.push({
            id,
            name: String(group.name || "").trim() || "Untitled group",
            parentId: group.parentId ? String(group.parentId) : null,
            puzzleIds: asArray(group.puzzleIds)
                .map(String)
                .filter((puzzleId, index, all) => all.indexOf(puzzleId) === index),
            createdAt: group.createdAt || null,
        });
    });
    return sanitizeGroupParents(groups);
}

export function orderedGroupsForTree(groups) {
    const childrenOf = new Map();
    groups.forEach((group) => {
        const key = group.parentId || "";
        const list = childrenOf.get(key) || [];
        list.push(group);
        childrenOf.set(key, list);
    });
    const result = [];
    const walk = (parentKey, depth) => {
        (childrenOf.get(parentKey) || []).forEach((child) => {
            result.push({ ...child, depth });
            walk(child.id, depth + 1);
        });
    };
    walk("", 0);
    return result;
}

/** Nested tree for TreeView: `{ ...group, children: [] }[]` */
export function nestedGroupsForTree(groups) {
    const childrenOf = new Map();
    groups.forEach((group) => {
        const key = group.parentId || "";
        const list = childrenOf.get(key) || [];
        list.push({ ...group, children: [] });
        childrenOf.set(key, list);
    });
    const attach = (nodes) => {
        nodes.forEach((node) => {
            node.children = childrenOf.get(node.id) || [];
            attach(node.children);
        });
        return nodes;
    };
    return attach(childrenOf.get("") || []);
}

export function groupParentChoices(groups, groupId) {
    const blocked = new Set([String(groupId || "")]);
    const childrenOf = new Map();
    groups.forEach((group) => {
        if (!group.parentId) {
            return;
        }
        const list = childrenOf.get(group.parentId) || [];
        list.push(group.id);
        childrenOf.set(group.parentId, list);
    });
    const stack = [String(groupId || "")];
    while (stack.length) {
        const id = stack.pop();
        (childrenOf.get(id) || []).forEach((childId) => {
            if (!blocked.has(childId)) {
                blocked.add(childId);
                stack.push(childId);
            }
        });
    }
    return groups.filter((group) => !blocked.has(group.id));
}

export function withGroupParent(groups, groupId, parentId) {
    const nextParent = parentId ? String(parentId) : null;
    if (nextParent) {
        const allowed = new Set(groupParentChoices(groups, groupId).map((group) => group.id));
        if (!allowed.has(nextParent)) {
            return groups;
        }
    }
    return groups.map((group) => (
        group.id === groupId ? { ...group, parentId: nextParent } : group
    ));
}

export function groupsAfterDelete(groups, groupId) {
    const target = groups.find((group) => group.id === groupId);
    if (!target) {
        return groups;
    }
    return groups
        .filter((group) => group.id !== groupId)
        .map((group) => (
            group.parentId === groupId
                ? { ...group, parentId: target.parentId || null }
                : group
        ));
}

export function movePuzzleId(puzzleIds, puzzleId, direction) {
    const ids = asArray(puzzleIds).map(String);
    const index = ids.indexOf(String(puzzleId));
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= ids.length) {
        return ids;
    }
    const next = ids.slice();
    const [item] = next.splice(index, 1);
    next.splice(nextIndex, 0, item);
    return next;
}

export function groupBreadcrumb(groups, groupId) {
    const byId = new Map(groups.map((group) => [group.id, group]));
    const names = [];
    const seen = new Set();
    let current = byId.get(String(groupId || ""));
    while (current && !seen.has(current.id)) {
        seen.add(current.id);
        names.unshift(current.name);
        current = current.parentId ? byId.get(current.parentId) : null;
    }
    return names.join(" / ");
}

export function tutorStoryStatus(item) {
    return String(item?.status || "").toUpperCase() === "DELETED" ? "DELETED" : "ACTIVE";
}

function readStoryClues(raw) {
    if (!Array.isArray(raw)) {
        return [];
    }
    const seen = new Set();
    const clues = [];
    raw.forEach((row) => {
        const word = String(row?.word || "").trim();
        const definition = String(row?.definition || row?.meaning || "").trim();
        const key = word.toLowerCase();
        if (!word || !definition || seen.has(key)) {
            return;
        }
        seen.add(key);
        clues.push({ word, definition });
    });
    return clues;
}

/** Every saved story, including ones the tutor has deleted. */
export function readTutorLibraryAll(details) {
    return asArray(details?.[TUTOR_DETAILS_KEYS.library])
        .filter((item) => item && item.id)
        .map((item) => ({
            id: String(item.id),
            title: String(item.title || "").trim() || "Untitled crossword",
            sourceText: String(item.sourceText || ""),
            createdAt: item.createdAt || null,
            status: tutorStoryStatus(item),
            pdfUrl: item.pdfUrl ? String(item.pdfUrl) : "",
            clues: readStoryClues(item.clues),
        }));
}

/** Stories the tutor can see. Missing status counts as active. */
export function readTutorLibrary(details) {
    return readTutorLibraryAll(details).filter((item) => item.status !== "DELETED");
}

/** Keep soft-deleted rows when the visible library is saved. */
export function withDeletedStories(details, visibleItems) {
    const deleted = readTutorLibraryAll(details).filter((item) => item.status === "DELETED");
    const visible = asArray(visibleItems).map((item) => ({
        ...item,
        status: "ACTIVE",
        pdfUrl: item.pdfUrl ? String(item.pdfUrl) : "",
    }));
    const visibleIds = new Set(visible.map((item) => item.id));
    return [...visible, ...deleted.filter((item) => !visibleIds.has(item.id))];
}

export function groupsForLibraryItem(groups, puzzleId) {
    const id = String(puzzleId || "");
    if (!id) {
        return [];
    }
    return groups.filter((group) => group.puzzleIds.includes(id));
}

export function titleFromSourceText(sourceText) {
    const firstLine = String(sourceText || "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .find(Boolean);
    if (!firstLine) {
        return "Untitled crossword";
    }
    return firstLine.length > 80 ? `${firstLine.slice(0, 77)}…` : firstLine;
}
